import type { Copy } from "./i18n";
import type { DiscoverGroup } from "./types";

/**
 * Chip-urile de descoperire pe două niveluri: tipul de produs („Sandale"), apoi
 * publicul care chiar există la tipul acela („Toate / Dama / Bărbați / Copii").
 *
 * Logica stă aici, fără DOM, ca să poată fi testată pe date reale. Widgetul doar o
 * randează. Numele tipului vine din catalog și NU se traduce; doar eticheta
 * publicului se traduce, fiindcă e singura care nu vine din date.
 */

export interface DiscoveryAudienceChip {
  sizes?: string[];
  /** Cheia publicului din catalog; gol pentru chip-ul „Toate". */
  key: string;
  label: string;
  query: string;
  /** Sursa trimisă către analytics: „type:all" sau „type:dama". */
  source: string;
}

export interface DiscoveryTypeChip {
  key: string;
  label: string;
  count: number;
  audiences: DiscoveryAudienceChip[];
}

export interface DiscoveryCategoryChip {
  key: string;
  label: string;
  types: DiscoveryTypeChip[];
}

/** Only fashion catalogs get the fashion hierarchy; other stores keep their types. */
export function discoveryCategoryChips(types: DiscoveryTypeChip[]): DiscoveryCategoryChip[] | null {
  const categories: DiscoveryCategoryChip[] = [
    { key: "footwear", label: "Încălțăminte", types: [] },
    { key: "clothing", label: "Îmbrăcăminte", types: [] },
    { key: "accessories", label: "Accesorii", types: [] },
  ];
  const other: DiscoveryTypeChip[] = [];
  for (const type of types) {
    const name = type.key.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    // Care products must be classified before footwear ("ingrijirea incaltamintei").
    const category = /ingrij|crem|brant|siret|talpic|accesori|geanta|genti|poset|rucsac|portofel|borset|troler|valiz|curea|curele|esarfe|bijut|soset|ciorap/.test(name) ? categories[2]
      : /incalt|pantof|adidas|sneaker|sandal|ghete|botin|cizm|papuc|sabot|mocasin|balerin|espadril/.test(name) ? categories[0]
      : /imbrac|roch|bluz|camas|pantalon|fust|tricou|pulover|sacou|jachet|geci|geaca|palto|trening|hanorac|vesta|veste|bermud|costum|salopet/.test(name) ? categories[1] : undefined;
    (category?.types ?? other).push(type);
  }
  if (!categories.some((category) => category.types.length)) return null;
  return [...categories.filter((category) => category.types.length),
    ...(other.length ? [{ key: "other", label: "Alte produse", types: other }] : [])];
}

/** Eticheta publicului în limba vizitatorului. Cheie necunoscută → cheia brută. */
export function audienceLabel(copy: Copy, key: string): string {
  if (key === "dama") return copy.audienceDama;
  if (key === "barbati") return copy.audienceBarbati;
  if (key === "copii") return copy.audienceCopii;
  return key;
}

/**
 * Nivelul 1. Întoarce `null` când `groups` lipsește sau e gol — atunci widgetul cade
 * înapoi pe vechiul rând plat de `chips`, ca să nu rupă un API mai vechi sau alt magazin.
 */
export function discoveryTypeChips(
  groups: DiscoverGroup[] | undefined,
  copy: Copy,
  limit = 8,
): DiscoveryTypeChip[] | null {
  if (!groups || groups.length === 0) return null;
  return groups.slice(0, limit).map((group) => ({
    key: group.key,
    // Numele tipului vine din catalog: se afișează exact cum a venit.
    label: group.key,
    count: group.count,
    audiences: [
      {
        key: "",
        label: copy.audienceAll,
        // „Toate" caută tipul fără gen, ca produsele fără gen cunoscut să rămână accesibile.
        query: group.key.toLowerCase(),
        source: "type:all",
        sizes: group.sizes,
      },
      ...(group.audiences ?? []).map((audience) => ({
        key: audience.key,
        label: audienceLabel(copy, audience.key),
        // Interogarea e gata compusă de server („sandale dama"); widgetul nu o recompune,
        // ca să nu ajungă să caute altceva decât a numărat serverul.
        query: audience.query,
        source: `type:${audience.key}`,
        sizes: audience.sizes,
      })),
    ],
  }));
}
